import { BadgeCheck, Bell, CalendarClock, MapPinOff, MoreHorizontal, ReceiptText, ShieldAlert, UserCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { timeAgo, type AppNotification } from './queries';

const KIND: Record<AppNotification['kind'], { icon: typeof Bell; tone: string }> = {
  certification: { icon: BadgeCheck, tone: 'bg-digital-50 text-digital-600' },
  sign_on: { icon: MapPinOff, tone: 'bg-danger-50 text-danger-600' },
  bill: { icon: ReceiptText, tone: 'bg-signal-50 text-signal-700' },
  invitation: { icon: UserCheck, tone: 'bg-violet-50 text-violet-600' },
  reminder: { icon: CalendarClock, tone: 'bg-gold-50 text-gold-700' },
  expiring: { icon: ShieldAlert, tone: 'bg-gold-50 text-gold-700' },
  general: { icon: Bell, tone: 'bg-cloud text-muted' },
};

/** One row, shared by the bell's dropdown and the full notifications page. */
export function NotificationItem({
  notification,
  onOpen,
  onToggleRead,
  onRemove,
}: {
  notification: AppNotification;
  onOpen: () => void;
  onToggleRead: () => void;
  onRemove: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { icon: Icon, tone } = KIND[notification.kind] ?? KIND.general;

  useEffect(() => {
    if (!menu) return undefined;
    const close = (event: PointerEvent) => !menuRef.current?.contains(event.target as Node) && setMenu(false);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [menu]);

  return (
    <li className={cn('group relative', !notification.read && 'bg-digital-50/40')}>
      <button type="button" onClick={onOpen} className="flex w-full gap-3 px-4 py-3 pr-12 text-left transition-colors hover:bg-cloud">
        <span className={cn('relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full', tone)}>
          <Icon aria-hidden className="h-[18px] w-[18px]" />
          {notification.count > 1 && (
            <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-navy px-1 text-center text-2xs font-semibold leading-[18px] text-white">
              {notification.count > 99 ? '99+' : notification.count}
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block text-sm text-ink', !notification.read ? 'font-semibold' : 'font-medium')}>{notification.title}</span>
          <span className="mt-0.5 block text-helper text-body">{notification.body}</span>
          <span className={cn('mt-1 block text-2xs', !notification.read ? 'font-semibold text-digital' : 'text-muted')}>{timeAgo(notification.created_at)}</span>
        </span>
      </button>

      {!notification.read && <span aria-label="Unread" className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-digital group-hover:hidden" />}

      <div ref={menuRef} className="absolute right-2 top-2.5">
        <button
          type="button"
          aria-label="Notification options"
          aria-expanded={menu}
          onClick={() => setMenu((value) => !value)}
          className={cn('flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-white hover:text-body', menu ? 'flex' : 'hidden group-hover:flex focus:flex')}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        {menu && (
          <div role="menu" className="absolute right-0 top-full z-10 mt-1 w-44 rounded-lg border border-line bg-white p-1 shadow-flyout">
            <button type="button" role="menuitem" className="w-full rounded px-2.5 py-1.5 text-left text-sm hover:bg-cloud" onClick={() => { setMenu(false); onToggleRead(); }}>
              {notification.read ? 'Mark as unread' : 'Mark as read'}
            </button>
            <button type="button" role="menuitem" className="w-full rounded px-2.5 py-1.5 text-left text-sm text-danger-600 hover:bg-danger-50" onClick={() => { setMenu(false); onRemove(); }}>
              Remove
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
